import { useQuery } from "@tanstack/react-query";
import { getRebuildAPI } from "@rebuild/shared/api";
import { JobRecordStatus, type JobRecord } from "@rebuild/shared/models";
import { JOB_KEYS } from "./queryKeys";
import { POLL_INTERVAL_MS, isJobActive } from "./useJobStatus";

const { jobControllerGetJobByJobId } = getRebuildAPI();

const MAX_STATUS_FETCH_ATTEMPTS = 10;

interface JobProgress {
  job: JobRecord | undefined;
  isTerminal: boolean;
  failed: boolean;
  error: JobRecord["error"];
  statusUnavailable: boolean;
}

/**
 * Polls one known job until it reaches a terminal state. Prefer this over `useEstimateJobStatus` when
 * waiting on a job you just enqueued: the estimate-wide list aggregates every job the estimate has ever
 * had, so it cannot tell your job's outcome apart from an unrelated older one's.
 */
export function useJob(jobId: string | undefined): JobProgress {
  const { data, failureCount } = useQuery({
    queryKey: JOB_KEYS.detail(jobId ?? ""),
    queryFn: async (): Promise<JobRecord> => {
      const { data } = await jobControllerGetJobByJobId(jobId!);
      return data;
    },
    enabled: !!jobId,
    retry: MAX_STATUS_FETCH_ATTEMPTS - 1,
    retryDelay: POLL_INTERVAL_MS,
    refetchInterval: (query) => {
      if (query.state.fetchFailureCount >= MAX_STATUS_FETCH_ATTEMPTS) return false;
      const job = query.state.data;
      if (!job) return POLL_INTERVAL_MS;
      return isJobActive(job) ? POLL_INTERVAL_MS : false;
    },
    refetchIntervalInBackground: false,
  });

  const failed = data?.status === JobRecordStatus.failed;
  const isTerminal = failed || data?.status === JobRecordStatus.completed;
  return {
    job: data,
    isTerminal,
    failed,
    error: failed ? data?.error : undefined,
    statusUnavailable: !isTerminal && failureCount >= MAX_STATUS_FETCH_ATTEMPTS,
  };
}
